/* Phase 6 · Reusable widgets — MyKRA (any user) + DataCompleteness (director) */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Target, Award, AlertOctagon, ChevronRight } from "lucide-react";

const toneRing = {
  green: "ring-emerald-200",
  amber: "ring-amber-200",
  red: "ring-red-200",
};
const toneText = {
  green: "text-emerald-700",
  amber: "text-amber-700",
  red: "text-red-700",
};

export function MyKRA() {
  const [k, setK] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/kra/self").then(r => setK(r.data)).catch(() => setErr(true));
  }, []);
  if (err) return null;
  if (!k) return (
    <div className="bg-white rounded-lg border border-slate-100 p-3 text-xs text-[#7C8E9E]"
      data-testid="my-kra-loading">Loading KRA…</div>
  );
  return (
    <div className="bg-white rounded-lg border border-slate-100 p-4" data-testid="my-kra">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-14 h-14 rounded-full ring-4 ${toneRing[k.tone]} flex items-center justify-center bg-white`}>
          <div className={`text-xl font-black ${toneText[k.tone]}`}>{k.score}</div>
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E] flex items-center gap-1">
            <Target size={11} /> My Performance (KRA)
          </div>
          <div className={`text-sm font-semibold ${toneText[k.tone]}`}>
            {k.tone === "green" ? "On target" : k.tone === "amber" ? "Needs attention" : "Below target — action needed"}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {k.metrics.slice(0, 3).map((m, i) => (
          <div key={i} className="text-center">
            <div className="text-lg font-bold text-[#0B2340]">{m.value}</div>
            <div className="text-[10px] text-[#7C8E9E] leading-tight">{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DataCompletenessPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/data-completeness").then(r => setData(r.data));
  }, []);
  if (!data) return null;

  const missing = data.rows.filter(r => !r.uploaded);
  const complete = data.rows.length - missing.length;

  return (
    <div className="bg-white rounded-lg border border-slate-100 p-5 mt-6" data-testid="data-completeness">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E] flex items-center gap-2">
            <Award size={12} /> Data Completeness · master data
          </div>
          <h2 className="text-xl font-bold mt-1" style={{ color: "var(--brand-navy)" }}>
            {complete}/{data.rows.length} kinds have data
          </h2>
        </div>
        <Link to="/admin/csv-upload"
          data-testid="dc-upload-link"
          className="text-xs px-3 py-1.5 rounded-full border border-[#1B3A5C]/20 hover:bg-[#FAF6F0]"
          style={{ color: "var(--brand-navy)" }}>
          Bulk import →
        </Link>
      </div>

      {data.callouts.length > 0 && (
        <div className="mb-3 p-3 rounded bg-red-50 border border-red-200"
          data-testid="dc-callouts">
          <div className="flex items-center gap-1 text-xs font-semibold text-red-800 mb-1">
            <AlertOctagon size={12} /> Missing data — pending owners
          </div>
          <ul className="text-xs text-red-900 space-y-1">
            {data.callouts.map(co => (
              <li key={co.role} data-testid={`dc-callout-${co.role}`}>
                <span className="font-semibold">{co.names.join(", ") || co.role}</span>
                {" "}({co.role}) has not uploaded{" "}
                <span className="italic">{co.missing_kinds.map(k => k.label).join(", ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {data.rows.map(r => (
          <div key={r.kind} data-testid={`dc-row-${r.kind}`}
            className={`flex items-center justify-between p-2 rounded border ${r.uploaded ? "border-emerald-200 bg-emerald-50/40" : "border-red-200 bg-red-50/40"}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#0B2340] truncate">{r.label}</div>
              <div className="text-[10px] text-[#7C8E9E]">
                owner: {r.owner_role} · {r.owner_names.join(", ") || "unassigned"}
              </div>
            </div>
            <div className="text-right ml-2 flex-shrink-0">
              <div className={`text-sm font-bold ${r.uploaded ? "text-emerald-700" : "text-red-700"}`}>
                {r.row_count}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-[#7C8E9E]">
                {r.uploaded ? "loaded" : "missing"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function KRAWeeklyRunButton() {
  const [running, setRunning] = useState(false);
  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/run-callouts");
      alert(`Pushed ${r.data.notifications} notifications`);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    }
    setRunning(false);
  };
  return (
    <button onClick={run} disabled={running} data-testid="kra-run-callouts"
      className="text-xs px-3 py-1.5 rounded-full border border-[#1B3A5C]/20 hover:bg-[#FAF6F0]">
      {running ? "Running…" : "Nudge everyone now"} <ChevronRight size={12} className="inline" />
    </button>
  );
}

// ------------- Missing-data banner (for the owner-role's own dashboard) ------
export function MyMissingDataBanner() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/data-completeness").then(async r => {
      const me = await api.get("/auth/me").catch(() => null);
      const myRole = me?.data?.role;
      const my = r.data.callouts.find(c => c.role === myRole);
      setItems(my?.missing_kinds || []);
    });
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="p-3 rounded bg-red-50 border border-red-200 mb-4 flex items-start gap-2"
      data-testid="my-missing-banner">
      <AlertOctagon size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-sm text-red-900">
        <div className="font-semibold">You have {items.length} master dataset{items.length === 1 ? "" : "s"} to upload</div>
        <div className="text-xs mt-0.5">
          {items.map(i => i.label).join(", ")}
        </div>
      </div>
      <Link to="/admin/csv-upload"
        className="text-xs px-3 py-1 rounded-full bg-red-600 text-white font-semibold flex-shrink-0"
        data-testid="my-missing-upload-btn">
        Upload now
      </Link>
    </div>
  );
}
